import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useVaultDashboard } from '../lib/vault/useVaultDashboard';
import { useAuth } from '../lib/auth/AuthProvider';
import { inviteRecipient, cancelRelease, type RecipientListItem, type ActiveRelease } from '../lib/vault/vaultApi';
import './vault.css';

export default function VaultScreen() {
  const dash = useVaultDashboard();
  const { user } = useAuth();

  if (dash.status === 'loading') {
    return <section className="vault"><p role="status" className="vault__kicker">Opening your vault…</p></section>;
  }
  if (dash.status === 'error') {
    return (
      <section className="vault">
        <p role="alert" className="vault__alert">{dash.error}</p>
        <button className="la-btn-secondary" onClick={() => void dash.reload()}>Try again</button>
      </section>
    );
  }
  if (dash.status === 'needs-vault') {
    return (
      <section className="vault">
        <p className="vault__kicker">Your legacy begins</p>
        <h1 className="vault__title">Open your vault.</h1>
        <p className="vault__sub">This is where the messages you leave for the people you love will live, sealed until their moment.</p>
        <div className="la-cta-wrap">
          <button className="la-btn-primary" onClick={() => void dash.createVault()}>Create your vault</button>
        </div>
      </section>
    );
  }
  if (dash.status === 'pending-attestation') {
    return (
      <section className="vault vault__pending">
        <p className="vault__kicker">Almost there</p>
        <h1 className="vault__title">Your advisor is preparing your vault.</h1>
        <p className="vault__sub">You'll be able to begin the moment they finish. This usually takes a little while.</p>
        <button className="la-btn-secondary" onClick={() => void dash.reload()}>Check again</button>
      </section>
    );
  }

  // ready
  const { aggregates, recipients } = dash;
  return (
    <section className="vault">
      {dash.activeReleases.length > 0 && dash.vault && (
        <StopReleaseBanner
          release={dash.activeReleases[0]}
          vaultId={dash.vault.id}
          onStopped={() => void dash.reload()}
        />
      )}
      {dash.firm ? (
        <div className="vault__cobrand">
          {dash.firm.logo_url ? (
            <img className="vault__cobrandLogo" src={dash.firm.logo_url} alt={dash.firm.name} />
          ) : (
            <p className="vault__kicker">{dash.firm.name}</p>
          )}
          <p className="vault__cobrandNote">Kept with {dash.firm.name}, on Legacy Architect</p>
        </div>
      ) : (
        <p className="vault__kicker">Legacy Architect</p>
      )}
      <h1 className="vault__title">Welcome back.</h1>
      <p className="vault__sub">{user?.email ? `Signed in as ${user.email}` : 'The future is listening.'}</p>

      <div className="vault__stats">
        <Stat value={aggregates?.recipient_count ?? 0} label="Recipients" />
        <Stat value={aggregates?.message_count ?? 0} label="Messages" />
        <Stat value={aggregates?.sealed_count ?? 0} label="Sealed" />
      </div>

      <p className="vault__section-label">Your recipients</p>
      {recipients.length === 0 ? (
        <p className="vault__empty">Your vault awaits its first name.</p>
      ) : (
        <div className="rgrid">
          {recipients.map((r) => (
            <RecipientRow
              key={r.id}
              r={r}
              vaultId={dash.vault!.id}
              messageCount={dash.messageCountByRecipient[r.id] ?? 0}
              onInvited={() => void dash.reload()}
            />
          ))}
        </div>
      )}

      <div className="la-cta-wrap" style={{ marginTop: 24 }}>
        <Link className="la-btn-primary" to="/recipients/new">Add a recipient</Link>
      </div>
    </section>
  );
}

/**
 * The creator's veto, made real IN the app. Stage 3 made sure the owner is
 * NOTIFIED the instant someone tries to release their vault; this is the STOP
 * button that notification promised. It is deliberately the loudest thing on the
 * screen — someone is asserting you have died, and you are reading this, so you
 * are not; one press halts it.
 */
function StopReleaseBanner({ release, vaultId, onStopped }: { release: ActiveRelease; vaultId: string; onStopped: () => void }) {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function stop() {
    setBusy(true); setError(null);
    try {
      await cancelRelease(api, vaultId, release.id);
      onStopped();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not stop the release. Try again.');
      setBusy(false);
    }
  }

  return (
    <div className="stopbanner" role="alert">
      <p className="stopbanner__title">Someone asked to release your vault.</p>
      <p className="stopbanner__body">
        {release.authority === 'head_office'
          ? 'Legacy Architect head office has opened a release of your vault, believing you have died. '
          : `${
              release.initiated_by_name
                ? `${release.initiated_by_name} `
                : release.authority === 'executor'
                  ? 'Your named executor '
                  : 'A guardian '
            }${
              release.authority === 'executor'
                ? 'authorized the release of your vault because they believe you have died. '
                : 'began an emergency release because they believe you have died. '
            }`}
        If you are reading this, you are not. Press STOP to halt it now.
      </p>
      {error && <p className="stopbanner__error">{error}</p>}
      <button className="stopbanner__btn" onClick={() => void stop()} disabled={busy}>
        {busy ? 'Stopping…' : 'STOP this release'}
      </button>
    </div>
  );
}

function Stat({ value, label }: { value: number; label: string }) {
  return (
    <div className="vault__stat">
      <div className="vault__stat-value">{value}</div>
      <div className="vault__stat-label">{label}</div>
    </div>
  );
}

/**
 * A recipient, and — the part that was missing — whether we can actually REACH
 * them. A message sealed for someone whose email is unconfirmed never arrives:
 * the delivery worker will not send to an unverified contact. That was silent.
 * Here it is said out loud, and the fix is one button away.
 */
function RecipientRow({
  r,
  vaultId,
  messageCount,
  onInvited,
}: {
  r: RecipientListItem;
  vaultId: string;
  messageCount: number;
  onInvited: () => void;
}) {
  const { api } = useAuth();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function send() {
    setSending(true);
    setError(null);
    try {
      await inviteRecipient(api, vaultId, r.id);
      onInvited();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'We couldn’t send that invitation.');
    } finally {
      setSending(false);
    }
  }

  const reach = reachability(r);

  return (
    <div className="rrow">
      <div className="rrow__body">
        <div className="rrow__name">{r.full_name}</div>
        <div className="rrow__meta">
          {r.relationship}
          {r.is_placeholder && <span className="rrow__badge"> · {r.placeholder_label ?? 'Placeholder'}</span>}
          {r.is_minor && <span className="rrow__badge"> · Minor</span>}
        </div>
        <div className={`rrow__reach rrow__reach--${reach.tone}`}>{reach.label}</div>
        {error && <div className="rrow__error" role="alert">{error}</div>}
      </div>
      {messageCount > 0 ? (
        <Link className="rrow__milestone rrow__milestone--link" to={`/recipients/${r.id}/messages`}>
          {messageCount} sealed {messageCount === 1 ? 'message' : 'messages'}
        </Link>
      ) : (
        <div className="rrow__milestone">No messages yet</div>
      )}
      <div className="rrow__actions">
        {reach.action === 'invite' && (
          <button className="la-btn-secondary" onClick={() => void send()} disabled={sending}>
            {sending ? 'Sending…' : 'Send confirmation'}
          </button>
        )}
        {reach.action === 'resend' && (
          <button className="la-btn-secondary" onClick={() => void send()} disabled={sending}>
            {sending ? 'Sending…' : 'Resend'}
          </button>
        )}
        <Link className="la-btn-secondary" to={`/recipients/${r.id}/edit`}>
          {reach.action === 'add-email' ? 'Add an email' : 'Edit'}
        </Link>
      </div>
    </div>
  );
}

type Reach = {
  label: string;
  tone: 'ready' | 'waiting' | 'blocked';
  action: 'invite' | 'resend' | 'add-email' | 'none';
};

/**
 * The one question that matters for a recipient row: if you sealed something for
 * this person today, would it reach them? Phrased as the answer, not as a status.
 *
 * MINORS deliver differently and MUST be judged differently — the delivery worker
 * ignores a minor's own contact entirely and sends to their guardian's email (no
 * confirmation code), or fails closed if there is none. Reading `contact_verified`
 * for a minor would tell the member the exact opposite of the truth, so the minor
 * branch comes first.
 */
function reachability(r: RecipientListItem): Reach {
  if (r.is_placeholder) {
    return { label: 'Not a real person yet — no one to reach', tone: 'blocked', action: 'none' };
  }
  if (r.is_minor) {
    return r.has_guardian_email
      ? { label: 'Confirmed — messages go to their parent or guardian', tone: 'ready', action: 'none' }
      : { label: 'Add a parent or guardian email so messages can be delivered', tone: 'blocked', action: 'add-email' };
  }
  if (r.contact_verified) {
    return { label: 'Confirmed — messages will reach them', tone: 'ready', action: 'none' };
  }
  if (!r.has_email) {
    return { label: 'No email on file — nothing can be delivered', tone: 'blocked', action: 'add-email' };
  }
  if (r.invite_status === 'pending') {
    return { label: 'Invitation sent — waiting for them to confirm', tone: 'waiting', action: 'resend' };
  }
  if (r.invite_status === 'expired') {
    return { label: 'Their invitation expired — send another', tone: 'blocked', action: 'resend' };
  }
  return { label: 'Not confirmed — a message would not arrive', tone: 'blocked', action: 'invite' };
}
